import React, { useState } from 'react'
import { 
  FiHome, 
  FiInfo, 
  FiMail, 
  FiDollarSign,
  FiEdit3,
  FiEye,
  FiSettings,
  FiNavigation,
  FiLayout,
  FiPlay,
  FiUsers,
  FiStar
} from 'react-icons/fi'
import { ContentSectionType } from '../../types/content'
import HeroEditor from './editors/HeroEditor'
import AboutEditor from './editors/AboutEditor'
import ContactEditor from './editors/ContactEditor'
import SiteSettingsEditor from './editors/SiteSettingsEditor'
import HeaderEditor from './editors/HeaderEditor'
import FooterEditor from './editors/FooterEditor'
import VideoSectionEditor from './editors/VideoSectionEditor'
import ArtistsEditor from './editors/ArtistsEditor'
import TestimonialsEditor from './editors/TestimonialsEditor'

const ContentManagement: React.FC = () => {
  const [selectedSection, setSelectedSection] = useState<ContentSectionType | null>(null)

  // Page sections in order they appear on the site
  const pageSections = [
    {
      type: 'header' as ContentSectionType,
      title: 'Header & Navigation',
      description: 'Site header, logo, navigation menu, and CTA button',
      icon: FiNavigation,
      stats: { fields: 4, lastUpdate: 'Never' }
    },
    {
      type: 'hero' as ContentSectionType,
      title: 'Hero Section',
      description: 'Main banner with title, description, and call-to-action buttons',
      icon: FiHome,
      stats: { fields: 7, lastUpdate: '2 days ago' }
    },
    {
      type: 'about' as ContentSectionType,
      title: 'What We Do',
      description: 'Intro section title, description, and venue logos',
      icon: FiInfo,
      stats: { fields: 5, lastUpdate: '1 week ago' }
    },
    {
      type: 'artists' as ContentSectionType,
      title: 'Artists Section',
      description: 'Call-to-action section for booking artists',
      icon: FiUsers,
      stats: { fields: 3, lastUpdate: 'Never' }
    },
    {
      type: 'testimonials' as ContentSectionType,
      title: 'Testimonials',
      description: 'Customer reviews and testimonials',
      icon: FiStar,
      stats: { fields: 3, lastUpdate: 'Never' }
    },
    {
      type: 'videos' as ContentSectionType,
      title: 'Video Section',
      description: 'Video gallery with performance recordings and artist content',
      icon: FiPlay,
      stats: { fields: 6, lastUpdate: 'Never' }
    },
    {
      type: 'contact' as ContentSectionType,
      title: 'Contact Section',
      description: 'Contact information, form, and social media links',
      icon: FiMail,
      stats: { fields: 8, lastUpdate: '3 days ago' }
    },
    {
      type: 'footer' as ContentSectionType,
      title: 'Footer',
      description: 'Footer content, contact info, social links, and quick links',
      icon: FiLayout,
      stats: { fields: 8, lastUpdate: 'Never' }
    }
  ]

  // Global settings sections
  const settingsSections = [
    {
      type: 'site-settings' as ContentSectionType,
      title: 'Site Settings',
      description: 'Global site settings including fonts and theme colors',
      icon: FiSettings,
      stats: { fields: 5, lastUpdate: 'Never' }
    }
  ]

  const getSectionDescription = (section: ContentSectionType) => {
    const descriptions: Record<ContentSectionType, string> = {
      hero: 'Edit the main banner content that visitors see first',
      about: 'Edit intro section title, description, and venue logos',
      header: 'Edit site header, logo, navigation menu, and call-to-action button',
      footer: 'Edit footer content, contact information, social links, and quick links',
      videos: 'Manage video gallery with performance recordings and artist content',
      artists: 'Edit the call-to-action section for booking artists',
      testimonials: 'Manage customer testimonials and reviews',
      contact: 'Edit contact information, form, and social media links',
      pricing: 'Edit pricing guide email capture form and messaging',
      'site-settings': 'Customize fonts and theme colors for your entire site'
    }
    return descriptions[section]
  }

  const handleSectionSelect = (sectionType: ContentSectionType) => {
    setSelectedSection(sectionType)
  }

  if (selectedSection) {
    return (
      <div className="space-y-6 relative">
        {/* Background Gradient Flares */}
        <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-gradient-to-br from-purple-200/80 to-blue-200/80 rounded-full blur-3xl -z-10 pointer-events-none"></div>
        <div className="absolute bottom-40 right-20 w-[500px] h-[500px] bg-gradient-to-tl from-blue-200/65 to-purple-200/65 rounded-full blur-3xl -z-10 pointer-events-none"></div>
        
        {/* Header with back button */}
        <div className="space-y-4">
          <button
            onClick={() => setSelectedSection(null)}
            className="text-neutral-600 hover:text-neutral-900 transition-colors text-sm font-medium inline-flex items-center space-x-1"
          >
            <span>←</span>
            <span>Back to Content Overview</span>
          </button>
          
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold text-neutral-900 capitalize mb-2">
              Edit {selectedSection} Section
            </h1>
            <p className="text-neutral-600 text-sm">{getSectionDescription(selectedSection)}</p>
          </div>
        </div>

        {/* Content Editor */}
        {selectedSection === 'hero' && (
          <HeroEditor />
        )}
        
        {selectedSection === 'about' && (
          <AboutEditor />
        )}
        
        {selectedSection === 'header' && (
          <HeaderEditor />
        )}
        
        {selectedSection === 'footer' && (
          <FooterEditor />
        )}
        
        {selectedSection === 'videos' && (
          <VideoSectionEditor />
        )}
        
        {selectedSection === 'artists' && (
          <ArtistsEditor />
        )}
        
        {selectedSection === 'testimonials' && (
          <TestimonialsEditor />
        )}
        
        {selectedSection === 'contact' && (
          <ContactEditor />
        )}
        
        {selectedSection === 'site-settings' && (
          <SiteSettingsEditor />
        )}
        
        {selectedSection === 'pricing' && (
          <div className="bg-white border border-neutral-200 rounded-lg p-8">
            <div className="text-center py-12">
              <FiDollarSign size={48} className="text-neutral-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-neutral-800 mb-2">
                Pricing Editor
              </h3>
              <p className="text-neutral-600 mb-6 text-sm">
                Pricing section editor coming soon!
              </p>
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-6 relative">
      {/* Background Gradient Flares */}
      <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-gradient-to-br from-purple-200/80 to-blue-200/80 rounded-full blur-3xl -z-10 pointer-events-none"></div>
      <div className="absolute bottom-40 right-20 w-[500px] h-[500px] bg-gradient-to-tl from-blue-200/65 to-purple-200/65 rounded-full blur-3xl -z-10 pointer-events-none"></div>
      
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold text-neutral-900 mb-1">Content Management</h1>
        <p className="text-neutral-600 text-sm">
          Edit and manage all the content on your website. Changes save automatically when you click the save button.
        </p>
      </div>

      {/* Page Content Sections */}
      <div>
        <h2 className="text-lg font-semibold text-neutral-800 mb-4">Page Sections</h2>
        <p className="text-sm text-neutral-600 mb-6">Edit content sections in the order they appear on your site</p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {pageSections.map((section) => (
            <div
              key={section.type}
              onClick={() => handleSectionSelect(section.type)}
              className="p-[1px] bg-gradient-to-br from-blue-500/50 to-purple-500/50 rounded-lg group cursor-pointer hover:shadow-lg transition-all"
            >
              <div className="bg-white rounded-lg hover:bg-gradient-to-br hover:from-white hover:to-neutral-50 transition-all"
            >
                <div className="p-6">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-3 bg-gradient-to-br from-blue-600 to-purple-600 text-white rounded-md shadow-md">
                      <section.icon size={20} />
                    </div>
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                      <FiEdit3 size={16} className="text-neutral-400" />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                      {section.title}
                    </h3>
                    <p className="text-neutral-600 text-sm leading-relaxed">
                      {section.description}
                    </p>
                  </div>

                  {/* Stats */}
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center space-x-3 text-neutral-500">
                      <span>{section.stats.fields} fields</span>
                      <span>•</span>
                      <span>Updated {section.stats.lastUpdate}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-blue-600 font-medium">
                      <span>Edit</span>
                      <span className="group-hover:translate-x-1 transition-transform">→</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Divider */}
      <div className="relative py-4">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-neutral-200"></div>
        </div>
        <div className="relative flex justify-center">
          <span className="bg-gradient-to-br from-blue-50/30 via-neutral-50 to-purple-50/30 px-4 text-sm text-neutral-500">Global Settings</span>
        </div>
      </div>

      {/* Global Settings Sections */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {settingsSections.map((section) => (
          <div
            key={section.type}
            onClick={() => handleSectionSelect(section.type)}
            className="p-[1px] bg-gradient-to-br from-indigo-500/50 to-purple-500/50 rounded-lg group cursor-pointer hover:shadow-lg transition-all"
          >
            <div className="bg-white rounded-lg hover:bg-gradient-to-br hover:from-white hover:to-neutral-50 transition-all"
          >
              <div className="p-6">
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="p-3 bg-gradient-to-br from-indigo-600 to-purple-600 text-white rounded-md shadow-md">
                    <section.icon size={20} />
                  </div>
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                    <FiEdit3 size={16} className="text-neutral-400" />
                  </div>
                </div>

                {/* Content */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                    {section.title}
                  </h3>
                  <p className="text-neutral-600 text-sm leading-relaxed">
                    {section.description}
                  </p>
                </div>

                {/* Stats */}
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center space-x-3 text-neutral-500">
                    <span>{section.stats.fields} fields</span>
                    <span>•</span>
                    <span>Updated {section.stats.lastUpdate}</span>
                  </div>
                  <div className="flex items-center space-x-2 text-indigo-600 font-medium">
                    <span>Edit</span>
                    <span className="group-hover:translate-x-1 transition-transform">→</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Quick Action */}
      <div className="p-[1px] bg-gradient-to-r from-indigo-500/50 to-purple-500/50 rounded-lg">
        <div className="bg-white rounded-lg p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-neutral-900 mb-1">Live Site</h2>
            <p className="text-sm text-neutral-600">Changes save instantly and appear on your live site</p>
          </div>
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center space-x-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-medium px-4 py-2.5 rounded-lg hover:shadow-md transition-all text-sm w-full sm:w-auto"
          >
            <FiEye size={18} />
            <span>View Site</span>
          </a>
        </div>
        </div>
      </div>
    </div>
  )
}

export default ContentManagement